import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Sun, CheckCircle, Clock, AlertTriangle, Camera, FileText, ClipboardCheck, Milestone } from 'lucide-react';
import axios from 'axios';

const API_URL = '/api';

export default function ClientPortalView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/client-portal/view/${token}`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.error || 'Link inválido o expirado'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return (
    <div className="cpv-loading"><div className="spinner" /><p>Cargando portal del proyecto...</p></div>
  );

  if (error) return (
    <div className="cpv-error">
      <Sun size={64} style={{ color: '#D4A017' }} />
      <h2>Acceso no disponible</h2>
      <p>{error}</p>
    </div>
  );

  const { project, taskStats, milestones, recentPhotos, documents, checklists, incidents } = data;
  const statusLabels = { pending: 'Pendiente', in_progress: 'En progreso', completed: 'Completado', on_hold: 'En espera' };
  const totalTasks = (taskStats || []).reduce((s, t) => s + parseInt(t.count), 0);
  const completedTasks = (taskStats || []).find(t => t.status === 'completed')?.count || 0;
  const taskPct = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  return (
    <div className="cpv-container">
      {/* Header */}
      <header className="cpv-header">
        <div className="cpv-logo"><Sun size={32} style={{ color: '#D4A017' }} /><span>Bitácora Solar</span></div>
      </header>

      {/* Project Overview */}
      <div className="cpv-hero">
        <h1>{project.name}</h1>
        <span className="cpv-code">{project.code}</span>
        <div className="cpv-hero-meta">
          {project.location && <span>📍 {project.location}</span>}
          {project.capacityKwp && <span>⚡ {project.capacityKwp} kWp</span>}
          <span className="cpv-status-badge">{statusLabels[project.status] || project.status}</span>
        </div>
      </div>

      {/* Progress */}
      <div className="cpv-section">
        <h2>Progreso General</h2>
        <div className="cpv-progress-ring-container">
          <svg viewBox="0 0 120 120" className="cpv-ring">
            <circle cx="60" cy="60" r="52" fill="none" stroke="#1e293b" strokeWidth="10" />
            <circle cx="60" cy="60" r="52" fill="none" stroke="#D4A017" strokeWidth="10"
              strokeDasharray={`${52 * 2 * Math.PI}`}
              strokeDashoffset={`${52 * 2 * Math.PI * (1 - (project.progress || 0) / 100)}`}
              strokeLinecap="round" transform="rotate(-90 60 60)" />
            <text x="60" y="56" textAnchor="middle" fill="#D4A017" fontSize="24" fontWeight="bold">{project.progress || 0}%</text>
            <text x="60" y="72" textAnchor="middle" fill="#94a3b8" fontSize="10">completado</text>
          </svg>
        </div>
        {taskStats && (
          <div className="cpv-task-bar">
            <div className="cpv-task-bar-fill" style={{ width: `${taskPct}%` }} />
            <span>{completedTasks}/{totalTasks} tareas completadas ({taskPct}%)</span>
          </div>
        )}
      </div>

      {/* Milestones */}
      {milestones && milestones.length > 0 && (
        <div className="cpv-section">
          <h2>Hitos Contractuales</h2>
          <div className="cpv-milestones">
            {milestones.map((m, i) => (
              <div key={i} className={`cpv-milestone ${m.status}`}>
                <div className="cpv-ms-icon">
                  {m.status === 'completed' ? <CheckCircle size={20} style={{ color: '#10B981' }} />
                    : m.status === 'in_progress' ? <Clock size={20} style={{ color: '#D4A017' }} />
                    : <Clock size={20} style={{ color: '#64748B' }} />}
                </div>
                <div className="cpv-ms-info">
                  <span className="cpv-ms-name">{m.name}</span>
                  <span className="cpv-ms-date">
                    {m.actual_date ? `✓ ${new Date(m.actual_date).toLocaleDateString('es')}`
                      : m.deadline_date ? `Plazo: ${new Date(m.deadline_date).toLocaleDateString('es')}` : ''}
                  </span>
                </div>
                {m.payment_percentage && <span className="cpv-ms-pct">{m.payment_percentage}%</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Photos */}
      {recentPhotos && recentPhotos.length > 0 && (
        <div className="cpv-section">
          <h2><Camera size={18} /> Galería del Proyecto</h2>
          <div className="cpv-photo-grid">
            {recentPhotos.map(p => (
              <div key={p.id} className="cpv-photo">
                <img src={p.thumbnail_url || p.file_url} alt={p.description || ''} loading="lazy" />
                {p.description && <span className="cpv-photo-desc">{p.description}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Documents */}
      {documents && documents.length > 0 && (
        <div className="cpv-section">
          <h2><FileText size={18} /> Documentos</h2>
          <div className="cpv-docs">
            {documents.map(d => (
              <a key={d.id} className="cpv-doc-row" href={d.file_url} target="_blank" rel="noopener noreferrer">
                <FileText size={16} style={{ color: '#D4A017' }} />
                <span>{d.name}</span>
                <span className="cpv-doc-meta">v{d.version}</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Checklists */}
      {checklists && checklists.length > 0 && (
        <div className="cpv-section">
          <h2><ClipboardCheck size={18} /> Verificaciones</h2>
          <div className="cpv-checks">
            {checklists.map((c, i) => {
              const pct = c.total_items > 0 ? Math.round(c.completed_items / c.total_items * 100) : 0;
              return (
                <div key={i} className="cpv-check-row">
                  <span className="cpv-check-name">{c.name}</span>
                  <div className="cpv-check-bar"><div style={{ width: `${pct}%` }} /></div>
                  <span className="cpv-check-pct">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Incidents summary */}
      {incidents && (
        <div className="cpv-section cpv-incidents-summary">
          <div className="cpv-inc-box">
            <AlertTriangle size={18} style={{ color: '#F59E0B' }} />
            <span>{incidents.open_incidents || 0} incidencias abiertas</span>
          </div>
          <div className="cpv-inc-box resolved">
            <CheckCircle size={18} style={{ color: '#10B981' }} />
            <span>{incidents.closed_incidents || 0} resueltas</span>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="cpv-footer">
        <Sun size={16} style={{ color: '#D4A017' }} />
        <span>Bitácora Solar — Powered by Energestics</span>
      </footer>
    </div>
  );
}
